import React, { useEffect, useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useNavigate, Link } from 'react-router-dom';
import fetch from 'unfetch';
import { css } from '@emotion/react';

import { ADMIN_ACCESS_TOKEN_KEY, ADMIN_EXPIRES_IN, getFetcher } from '../../App';
import { hasSessionState } from '@pages/Permissions/User';
import { dialogContentsState } from '@components/templates';
import Input from '@components/atoms/Inputs/Input';
import PrimaryButton from '@components/atoms/Inputs/Button/Primary';
import { ErrorCode, ErrorMessage } from '@variables/error';
import { LoginBody, LoginField, LoginFieldTitle, LockIcon, IdValidationStyle, PwValidationStyle } from './style';

interface LoginParams {
  userId: string;
  password: string;
}

interface LoginResponse {
  grantType: string;
  accessToken: string;
  accessTokenExpiresIn: number;
}

const REMEMBERED_ID_KEY = 'REMEMBERED_ID';
const ID_REGEX = /^([a-z|A-Z|0-9|!?@#$%^&*():;+-=~{}<>\_\[\]\|\\\"\'\,\.\/\`\₩]+)$/;

const fetcher = (url: string, params: LoginParams) =>
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  }).then((r) => {
    return r.json();
  });

const checkId = (id: string) => {
  if (!id) return 'none';

  if (!ID_REGEX.test(id)) {
    return 'invalid';
  }
  if (id.length < 3) {
    return 'short';
  }

  return '';
};
const LoginPage = () => {
  let navigate = useNavigate();

  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [isRememberId, setIsRememberId] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const memoizedParams = useMemo(() => {
    return { userId, password };
  }, [userId, password]);
  const [onCapsLock, setOnCapsLock] = useState(false);
  const setHasSession = useSetRecoilState(hasSessionState);
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const getMyData = async (token: string) => {
    const result = await getFetcher({
      method: 'GET',
      accessToken: token,
    })(`api/me`);
    if (result.code === '200') {
      // SCS 모바일 슈퍼 어드민 계정 대응 navigate
      if (result.result.role === 'SUPER') {
        navigate('/transfer');
      } else {
        navigate('/license');
      }
    }
  };

  useEffect(() => {
    (async () => {
      const token = (await localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY)) as string;
      if (!token) {
        return;
      }
      await getMyData(token);
    })();
  }, []);

  useEffect(() => {
    const rememberedId = localStorage.getItem(REMEMBERED_ID_KEY) as string;
    if (rememberedId) setUserId(rememberedId);
  }, []);

  const signIn = async () => {
    setIsLoading(true);
    const result = await getFetcher({ method: 'POST', body: memoizedParams })(`api/login`);
    if (result.code === '200') {
      const response = result.result as LoginResponse;
      localStorage.setItem(ADMIN_ACCESS_TOKEN_KEY, response.accessToken);
      localStorage.setItem(ADMIN_EXPIRES_IN, `${response.accessTokenExpiresIn}`);
      setHasSession(true);

      if (isRememberId) {
        localStorage.setItem(REMEMBERED_ID_KEY, userId);
      } else {
        localStorage.removeItem(REMEMBERED_ID_KEY);
      }
      return getMyData(response.accessToken);
    }
    setIsLoading(false);
    if (ErrorCode[result.code]) {
      // Dialog settings
      setDialogContents({
        type: 'error',
        header: `${ErrorCode[result.code]}`,
        body: <>{ErrorMessage[ErrorCode[result.code]]}</>,
        footer: <PrimaryButton onClick={() => setDialogContents({ type: 'hidden' })}>OK</PrimaryButton>,
      });
    }
  };

  /**
   * capslock handler
   * @param event
   */
  const isCapslockHandler = (event: any) => {
    if (event?.getModifierState) {
      const _onCapslock = event?.getModifierState('CapsLock');
      setOnCapsLock(_onCapslock);
    }
  };

  /**
   * lockmode 이벤트
   */
  useEffect(() => {
    window.addEventListener('keydown', isCapslockHandler);

    return () => {
      window.removeEventListener('keydown', isCapslockHandler);
    };
  }, []);

  return (
    <LoginBody>
      <LoginField>
        <LockIcon />
        <LoginFieldTitle>Login</LoginFieldTitle>
        <Input
          type={'text'}
          placeholder={'ID'}
          value={userId}
          onChange={(e) => {
            e.target.dataset['invalid'] = '';
            if (e.target.value.length > 50) {
              setUserId(e.target.value.slice(0, 50));
              return;
            }

            setUserId(e.target.value);
          }}
          onBlur={(e) => {
            if (!e.target.value) {
              e.target.dataset['invalid'] = '';
            } else {
              e.target.dataset['invalid'] = checkId(e.target.value);
            }
          }}
          styles={IdValidationStyle}
          required
        />
        <Input
          className={'pwd-input'}
          type={'password'}
          placeholder={'Password'}
          value={password}
          onChange={(e) => {
            if (e.target.value.length > 50) {
              setPassword(e.target.value.slice(0, 50));
              return;
            }

            setPassword(e.target.value);
          }}
          onKeyDown={(e: any) => {
            if (e?.getModifierState('CapsLock')) {
              e.target.dataset['invalid'] = 'capslock';
            } else {
              e.target.dataset['invalid'] = '';
            }

            if (e.key === 'Enter' && userId.length && password.length) signIn();
          }}
          onFocus={(e) => {
            if (onCapsLock) {
              e.target.dataset['invalid'] = 'capslock';
            } else {
              e.target.dataset['invalid'] = '';
            }
          }}
          onBlur={(e) => {
            if (!e.target.value) {
              e.target.dataset['invalid'] = 'true';
              return;
            }
            e.target.dataset['invalid'] = '';
          }}
          styles={PwValidationStyle}
          required
        />
        <PrimaryButton
          styles={css`
            width: 221px;
            height: 48px;
            font-size: 18px;
            :disabled {
              color: rgba(255, 255, 255, 0.5);
            }
          `}
          data-loading={isLoading}
          disabled={!!checkId(userId) || !password.length || isLoading}
          onClick={signIn}
        >
          Sign in
        </PrimaryButton>
        <Input type={'checkbox'} checked={isRememberId} onChange={(e) => setIsRememberId(e.target.checked)}>
          Remember my user ID
        </Input>

        <Link to={'/register'} className={'go_license'}>
          First time registering a license
        </Link>
      </LoginField>
    </LoginBody>
  );
};

export default LoginPage;
